/**
 * Parse @[Display](kind:id) mentions in message body and render as
 * rich clickable chips. Entity types get colored icons.
 */

import { Link } from 'react-router-dom'
import { Icon } from '@/components/ui/icon'

const MENTION_RE = /@\[([^\]]+)\]\((\w+):([^)]+)\)/g

function hrefForMention(kind: string, id: string): string | null {
  switch (kind) {
    case 'customer':
      return `/customers/${id}`
    case 'vps':
      return `/services/vps`
    case 'payment':
      return `/finance/payments`
    case 'subscription':
      return `/subscriptions`
    default:
      return null
  }
}

function iconForKind(kind: string): string {
  switch (kind) {
    case 'user': return 'user'
    case 'customer': return 'user-tag'
    case 'vps': return 'server'
    case 'payment': return 'credit-card'
    case 'subscription': return 'layer-group'
    default: return 'link'
  }
}

function colorForKind(kind: string): string {
  switch (kind) {
    case 'customer': return 'bg-brand-500/15 text-brand-400 border-brand-500/20'
    case 'vps': return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20'
    case 'payment': return 'bg-amber-500/15 text-amber-400 border-amber-500/20'
    case 'user': return 'bg-primary/15 text-primary border-primary/20'
    default: return 'bg-ink-200/15 text-ink-500 border-ink-200/20'
  }
}

export function renderMessageBody(body: string): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  const re = new RegExp(MENTION_RE.source, 'g')
  while ((match = re.exec(body)) !== null) {
    if (match.index > lastIndex) {
      parts.push(body.slice(lastIndex, match.index))
    }
    const display = match[1]
    const kind = match[2]
    const id = match[3]
    const href = hrefForMention(kind, id)
    const colors = colorForKind(kind)

    const chip = (
      <span
        key={`${kind}:${id}:${match.index}`}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] font-medium ${colors}`}
      >
        <Icon name={iconForKind(kind)} variant="solid" className="text-[9px]" />
        {display}
      </span>
    )

    if (href) {
      parts.push(
        <Link
          key={`link-${kind}:${id}:${match.index}`}
          to={href}
          className="inline hover:opacity-80 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          {chip}
        </Link>,
      )
    } else {
      parts.push(chip)
    }
    lastIndex = re.lastIndex
  }

  if (lastIndex < body.length) {
    parts.push(body.slice(lastIndex))
  }

  return parts
}
